import { useAppSelector } from "@web-solutions/store/types"
import { useRef, useState } from "react";

import { useDispatch } from "react-redux";

import DeviceProps from "@magnus/react-native-device-props";

import Analytics from "@web-solutions/module-analytics";

import { PaymentProcessor, PaymentProcessorRef } from "core/payment";

import { extractDeclineCode } from "core/payment/utils/extract-decline-code";

import { changePurchaseTokenId } from "core/store/billing/actions";
import { selectPaymentSystem } from "core/store/billing/selectors";

import { processPayment } from "../api/payment";
import { recordTransaction } from "../api/transaction";
import { Purchase } from "../api/types";
import { markUpsaleAsPurchased, setIsShowPopup } from "../store";

export const useUpsalePurchase = () => {
  const ref = useRef<PaymentProcessorRef>(null);

  const dispatch = useDispatch();
  const { orderDetails } = useAppSelector((state) => state.billing);
  const { purchase } = useAppSelector((state) => state.billing);
  // @ts-ignore
  const unavailableProduct = useAppSelector(
    (state) => state.remoteConfig.unavailableProduct,
  );
  const paymentSystem = useAppSelector((state) => selectPaymentSystem(state as any));
  // The provider error itself (code / decline_code). Kept in a ref because the
  // charge is retried in a loop and the caller reads it right after the last
  // failed await, before a state update would be visible.
  const lastPurchaseErrorRef = useRef<any>(null);
  const [upsalePurchaseError, setUpsalePurchaseError] = useState<
    string | undefined
  >();

  const handleSaveTokenOneTime = (tokenId: string) => {
    dispatch(changePurchaseTokenId(tokenId));
  };

  const handleUpsalePurchase = async ({
    recurlyId,
    upsaleName,
    isNextStepBlocked,
    tokenId,
    threeDSecureActionResultTokenId,
    noToast,
  }: {
    recurlyId: string;
    upsaleName: string;
    isNextStepBlocked?: boolean;
    tokenId?: string;
    threeDSecureActionResultTokenId?: string;
    /** Set by screens that show the failure themselves, to avoid a double error. */
    noToast?: boolean;
  }) => {
    const purchaseData: Purchase = {
      email: purchase?.email,
      token_id: tokenId || purchase?.token_id,
      three_d_secure_action_result_token_id: threeDSecureActionResultTokenId,
    };

    if (unavailableProduct.includes(upsaleName)) {
      dispatch(setIsShowPopup({ isShow: true, upsaleName, isNextStepBlocked }));

      Analytics.trackEvent("upsale_popup_fail", "opened", {
        upsaleID: upsaleName,
      });

      return;
    }

    try {
      const idfm = await DeviceProps.getIDFM();
      const chargeInvoice = await processPayment(purchaseData, recurlyId, { noToast });
      await recordTransaction(chargeInvoice, idfm, purchaseData);

      lastPurchaseErrorRef.current = null;
      dispatch(markUpsaleAsPurchased(upsaleName));

      Analytics.trackEvent("purchase_upsale", "success", {
        product_id: recurlyId,
      });
      return chargeInvoice;
    } catch (error: any) {
      Analytics.trackEvent("purchase_upsale", "failed", {
        product_id: recurlyId,
        error: error.message,
        code: error?.code,
        decline_code: extractDeclineCode(error, paymentSystem),
      });
      lastPurchaseErrorRef.current = error;
      setUpsalePurchaseError(error.message);
    }
  };

  const renderPaymentProcessor = ({
    activeProduct,
    subscribeRecurlyId,
    oneTimeRecurlyId,
    isOnlyOneTimePurshaseUpsale = true,
    isRedirectToLastScreen = false,
    isOnlyCard = false,
    onCardCollected,
  }: {
    activeProduct: any;
    subscribeRecurlyId?: string;
    oneTimeRecurlyId?: string;
    isOnlyOneTimePurshaseUpsale?: boolean;
    isRedirectToLastScreen?: boolean;
    isOnlyCard?: boolean;
    /**
     * Charge the collected payment method yourself instead of letting the window
     * do it — that's how the sheet's retry pays the offer with a new card.
     */
    onCardCollected?: (formData: any) => void | Promise<any>;
  }) => {
    return (
      <PaymentProcessor
        ref={ref}
        orderDetails={orderDetails}
        activeProduct={activeProduct}
        onSuccess={() => {}}
        hidePayPal={false}
        isOnlyCard={isOnlyCard}
        isUpsaleOneTimePayment={true}
        onSaveTokenOneTime={handleSaveTokenOneTime}
        onUpsaleCardCollected={onCardCollected}
        oneTimeRecurlyId={oneTimeRecurlyId}
        subscribeRecurlyId={subscribeRecurlyId}
        isOnlyOneTimePurshaseUpsale={isOnlyOneTimePurshaseUpsale}
        isRedirectToLastScreen={isRedirectToLastScreen}
      />
    );
  };

  const showPaymentPopup = () => {
    ref.current?.showPaymentPopup();
  };

  const closePaymentPopup = () => {
    ref.current?.closePaymentPopup();
  };

  return {
    handleUpsalePurchase,
    upsalePurchaseError,
    /** Provider error of the last failed charge — carries code / decline_code. */
    getLastPurchaseError: () => lastPurchaseErrorRef.current,
    renderPaymentProcessor,
    showPaymentPopup,
    closePaymentPopup,
  };
};
